import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"

import BigNumber from "bignumber.js"
import { useAtom, useAtomValue } from "jotai"
import { NumberFormatValues } from "react-number-format"

import { appBorrowRepayPopupState } from "@/atoms/appPopupState"
import { modalAmountAtom, selectedReserveAtom } from "@/atoms/modal"
import { selectedMainObligationAtom } from "@/atoms/obligations"
import { currentSlotAtom, rateLimiterAtom } from "@/atoms/pools"
import { networkAtom } from "@/atoms/settings"
import { walletAssetsAtom } from "@/atoms/wallet"
import { LiquidationProgress } from "@/components/liquidationProgress"
import { RelendModal } from "@/components/relendModal"
import EmptyRelendModalBody from "@/components/relendModal/emptyRelendModalBody"
import useTrans from "@/hooks/useTrans"
import { BORROW, DEFAULT_DECIMALS, IS_MAINNET, RENEC, REPAY } from "@/utils/constants"
import { HelperText, SelectItem, TransactionDetail } from "@/utils/dataTypes"
import { Box, Tab, Tabs, Typography } from "@mui/material"

import { appModalDisableState } from "@/atoms/appModalDisableState"
import { referralAtom } from "@/atoms/referralState"
import { selectedPoolAtom } from "@/atoms/shared"
import { supplyToEarnAPYAtom, supplyToEarnRewardAtom } from "@/atoms/supplyToEarnState"
import Button from "@/components/base/Button"
import { ModalTxResult } from "@/components/modalTxResult"
import useActionOnChain from "@/hooks/useActionOnchain"
import useCheckingOnChain from "@/hooks/useCheckingOnChain"
import { NFTDetailType } from "@/hooks/useFetchNFT"
import useLocalStorage from "@/hooks/useLocalStorage"
import { useTransactionHandler } from "@/hooks/useTransactionHandler"
import useWalletConnect from "@/hooks/useWalletConnect"
import { borrowConfigs } from "@/utils/actions/borrow"
import { REPAY_MAX_LIMIT_ERROR_PERCENTAGE } from "@/utils/actions/commons"
import { repayConfigs } from "@/utils/actions/repay"
import { isStringEmpty } from "@/utils/helpers"
import { getTokenLogo } from "@/utils/logo"
import { RplToken } from "@/utils/modals/RplToken"
import { sendEventLog } from "@/utils/tracking-events"
import {
  WHITELIST_SYMBOL_BORROW_ACTIONS,
  WHITELIST_SYMBOL_REPAY_ACTIONS,
  isSymbolWhitelisted,
} from "@/utils/whitelists"
import { ActionEnum, BlockchainNetwork, ConstantsHelper, SOL } from "@renec-foundation/relend-adapter"
import { PublicKey } from "@solana/web3.js"
import { ModalInput, ModalNFT } from ".."
import { MODAL_ABORT_CONTROLLER_MESSAGE, MODAL_ABORT_CONTROLLER_TIMEOUT, MODEL_DISABLE_TIMEOUT } from "../constants"
import BorrowRepayModelStat from "./BorrowRepayModalStat"
import * as styles from "./styles"

export enum NFTVoucherType {
  BORROW = "borrowVoucher",
  REPAY = "repayVoucher",
}

export type SelectedNFTType = {
  [NFTVoucherType.BORROW]?: NFTDetailType
  [NFTVoucherType.REPAY]?: NFTDetailType
}

const View = () => {
  const trans = useTrans()
  const { address, getBalanceNativeToken, isRenecChain, isSolanaChain } = useWalletConnect()
  const [popupState, setPopupState] = useAtom(appBorrowRepayPopupState)
  const [tab, setTab] = useState(popupState.type)
  const [loading, setLoading] = useState<boolean | undefined>(false)
  const [helperText, setHelperText] = useState<HelperText>({
    content: "",
    color: "",
  })
  const [nftErrorText, setNftErrorText] = useState<string>()
  const [selectedReserveBalance, setSelectedReserveBalance] = useState<number | undefined>(1)

  const [selectedNFT, setSelectedNFT] = useState<SelectedNFTType>({
    [NFTVoucherType.BORROW]: undefined,
    [NFTVoucherType.REPAY]: undefined,
  })
  const [shouldRefreshNftCheckbox, refreshNFTCheckbox] = useState<boolean>(false)

  const refNFTInterestAmount = useRef<{
    [NFTVoucherType.BORROW]: number
    [NFTVoucherType.REPAY]: number
  }>({
    [NFTVoucherType.BORROW]: 0,
    [NFTVoucherType.REPAY]: 0,
  })

  const [trackingUTM] = useLocalStorage("trackingUTM", "{}")

  // hooks
  const { actionHandler } = useActionOnChain()
  const { getTokenAccountInfo } = useCheckingOnChain()
  const { handleTransactionResult } = useTransactionHandler()

  // atoms
  const [modalDisableState, setModalDisableState] = useAtom(appModalDisableState)
  const [amount, setAmount] = useAtom(modalAmountAtom)
  const [selectedReserve, setSelectedReserve] = useAtom(selectedReserveAtom)
  const [walletAssets] = useAtom(walletAssetsAtom)
  const [selectedObligation] = useAtom(selectedMainObligationAtom)
  const [selectedPool] = useAtom(selectedPoolAtom)
  const [rateLimiter] = useAtom(rateLimiterAtom)
  const [currentSlot] = useAtom(currentSlotAtom)
  const referral = useAtomValue(referralAtom)
  const supplyToEarnApys = useAtomValue(supplyToEarnAPYAtom)
  const supplyToEarnRewards = useAtomValue(supplyToEarnRewardAtom)

  // switch network
  const network = useAtomValue(networkAtom)

  // abort controller to control open and close model actions
  const [abortController, setAbortController] = useState(new AbortController())

  const isBorrowing = popupState.type === BORROW
  const isRepaying = popupState.type === REPAY
  const hasZeroBalance = !selectedReserveBalance || selectedReserveBalance === 0

  // others
  const [progressWidth, setProgressWidth] = useState(0)
  const [transactionDetail, setTransactionDetail] = useState<TransactionDetail>({
    isError: true,
    isActive: false,
    ctaButtonLink: "",
    title: "",
    content: "",
  })

  // liquidation health bar
  const usedObligation = selectedObligation ?? {
    totalSupplyValue: new BigNumber(0),
    totalBorrowValue: new BigNumber(0),
    borrowLimit: new BigNumber(0),
    liquidationThreshold: new BigNumber(0),
    borrowOverSupply: new BigNumber(0),
    borrowLimitOverSupply: new BigNumber(0),
    liquidationThresholdFactor: new BigNumber(0),
    weightedTotalBorrowValue: new BigNumber(0),
    weightedBorrowUtilization: new BigNumber(0),
  }

  const borrowLimitOverSupply = usedObligation.totalSupplyValue.isZero()
    ? new BigNumber(0)
    : usedObligation.borrowLimit.dividedBy(usedObligation.totalSupplyValue)

  const weightedBorrowOverSupply = useMemo(() => {
    if (usedObligation.totalSupplyValue.isZero()) return new BigNumber(0)

    let weightedTotalBorrowValue = usedObligation.weightedTotalBorrowValue
    const inputedAmount = amount || 0
    const price = selectedReserve?.price ?? new BigNumber(0)
    const inputedAmountToPriceUsd = price.multipliedBy(inputedAmount)

    if (tab === BORROW) weightedTotalBorrowValue = weightedTotalBorrowValue.plus(inputedAmountToPriceUsd)
    if (tab === REPAY) weightedTotalBorrowValue = weightedTotalBorrowValue.minus(inputedAmountToPriceUsd)

    const value = weightedTotalBorrowValue.dividedBy(usedObligation.totalSupplyValue)
    if (value.gte(1)) return new BigNumber(1)
    if (value.lte(0)) return new BigNumber(0)

    return value
  }, [tab, amount, usedObligation.totalSupplyValue, usedObligation.weightedTotalBorrowValue, selectedReserve])

  const liquidationThresholdOverSupply = usedObligation.totalSupplyValue.isZero()
    ? new BigNumber(0)
    : usedObligation.liquidationThreshold.dividedBy(usedObligation.totalSupplyValue)

  // configs
  const borrowMax = useMemo(
    () =>
      selectedReserve
        ? borrowConfigs.calculateMax(selectedReserve, walletAssets, selectedObligation, rateLimiter)
        : BigNumber(0),
    [rateLimiter, selectedObligation, selectedReserve, walletAssets],
  )
  const repayMax = useMemo(
    () =>
      selectedReserve
        ? repayConfigs.calculateMax(network, selectedReserve, walletAssets, selectedObligation, currentSlot)
        : BigNumber(0),
    [selectedReserve, network, walletAssets, selectedObligation, currentSlot],
  )

  const LIST_SYMBOL_WHITE_LIST = isBorrowing ? WHITELIST_SYMBOL_BORROW_ACTIONS : WHITELIST_SYMBOL_REPAY_ACTIONS
  const isRenecLiquidity = selectedReserve?.symbol === RENEC && isRenecChain
  const isSolanaLiquidity = selectedReserve?.symbol === SOL && isSolanaChain

  const abortAndSetNewAbortController = useCallback(() => {
    abortController.abort(MODAL_ABORT_CONTROLLER_MESSAGE)
    // Timeout so that the code inside the function called this method can be executed
    setTimeout(() => {
      setAbortController(new AbortController())
    }, MODAL_ABORT_CONTROLLER_TIMEOUT)
  }, [abortController])

  const handleFetchReserveBalance = useCallback(async () => {
    if (address && selectedReserve) {
      const result =
        isRenecLiquidity || isSolanaLiquidity
          ? await getBalanceNativeToken(abortController)
          : (
              await getTokenAccountInfo(
                new PublicKey(address),
                new PublicKey(selectedReserve.mintAddress),
                abortController,
              )
            ).tokenBalance
      setSelectedReserveBalance(result)
    }
  }, [
    address,
    selectedReserve,
    abortController,
    getBalanceNativeToken,
    getTokenAccountInfo,
    isRenecLiquidity,
    isSolanaLiquidity,
  ])

  const handleAmountChange = useCallback(
    (values: NumberFormatValues) => {
      setAmount(values.floatValue)
      setHelperText({ content: "", color: "" })
    },
    [setAmount],
  )

  const handleUpdateNFTInterestAmount = useCallback((voucherType: NFTVoucherType, value: number) => {
    refNFTInterestAmount.current = {
      ...refNFTInterestAmount.current,
      [voucherType]: value,
    }
  }, [])

  const handleCloseModal = useCallback(() => {
    if (!loading && !modalDisableState) {
      // Abort the current operation when closing the modal
      abortAndSetNewAbortController()

      setPopupState((prev) => ({
        ...prev,
        isOpen: false,
      }))
      setHelperText({ content: "", color: "" })
      setAmount(0)
      setTransactionDetail({
        isActive: false,
        ctaButtonLink: "",
        title: "",
        content: "",
      })
    }
  }, [loading, modalDisableState, setAmount, abortAndSetNewAbortController, setPopupState])

  const handleChangeTab = useCallback(
    (_event: React.SyntheticEvent, newTab: typeof BORROW | typeof REPAY) => {
      if (isRepaying && newTab === BORROW) {
        sendEventLog("view_borrow_popup", {
          isMainnet: IS_MAINNET,
        })
      } else if (isBorrowing && newTab === REPAY) {
        sendEventLog("view_repay_popup", {
          isMainnet: IS_MAINNET,
        })
      }

      setTab(newTab)
      setPopupState((prev) => ({
        ...prev,
        type: newTab,
      }))
    },
    [isRepaying, isBorrowing, setPopupState],
  )

  const disableModal = useCallback(() => {
    setModalDisableState(true)

    setTimeout(() => {
      setModalDisableState(false)
    }, MODEL_DISABLE_TIMEOUT)
  }, [setModalDisableState])

  useEffect(() => {
    setTab(popupState.type)
  }, [popupState.type])

  useEffect(() => {
    handleFetchReserveBalance()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReserve, address])

  useEffect(() => {
    // check if current selectedReserve is in whitelist, if not then select default first whitelisted
    const isSelectedReserveInWhitelist =
      selectedReserve && isSymbolWhitelisted(selectedReserve.symbol, LIST_SYMBOL_WHITE_LIST)
    if (!isSelectedReserveInWhitelist && selectedPool) {
      const defaultReserve = selectedPool.reserves.find((r) => isSymbolWhitelisted(r.symbol, LIST_SYMBOL_WHITE_LIST))
      setSelectedReserve(defaultReserve?.address ?? "")
    }
  }, [LIST_SYMBOL_WHITE_LIST, popupState, selectedPool, selectedReserve, setSelectedReserve])

  const SELECT_ITEMS: SelectItem[] = useMemo(() => {
    if (!selectedPool) return []

    return selectedPool.reserves
      .map((r) => {
        const tokenDisplayName = RplToken.getTokenDisplayName(r.symbol, r.name)
        return {
          value: r.address,
          icon: getTokenLogo(r.symbol, r),
          iconName: tokenDisplayName,
          alt: r.symbol,
          symbol: r.symbol,
        }
      })
      .filter((r) => isSymbolWhitelisted(r.symbol, LIST_SYMBOL_WHITE_LIST))
  }, [selectedPool, LIST_SYMBOL_WHITE_LIST])

  const amountNotNaN = amount ?? 0
  const buttonError = useMemo(() => {
    if (!selectedReserve) return <></>

    return isBorrowing
      ? borrowConfigs.verifyAction(
          network,
          new BigNumber(amountNotNaN),
          selectedObligation,
          selectedReserve,
          walletAssets,
          rateLimiter,
          trans,
        )
      : repayConfigs.verifyAction(
          network,
          new BigNumber(amountNotNaN),
          selectedObligation,
          selectedReserve,
          walletAssets,
          trans,
          currentSlot,
        )
  }, [
    amountNotNaN,
    currentSlot,
    isBorrowing,
    network,
    rateLimiter,
    selectedObligation,
    selectedReserve,
    trans,
    walletAssets,
  ])

  const zeroBalanceGuideText = useMemo(
    () =>
      hasZeroBalance && isRepaying ? `${trans.dashboard.modal.not_enough_balance} ${selectedReserve?.symbol}` : null,
    [trans, hasZeroBalance, isRepaying, selectedReserve],
  )

  const submitButtonContent = useMemo(() => {
    switch (true) {
      case !!zeroBalanceGuideText:
        return zeroBalanceGuideText
      case !!buttonError:
        return buttonError
      case isBorrowing:
        return trans.dashboard.modal.borrow
      default:
        return trans.dashboard.modal.repay
    }
  }, [buttonError, isBorrowing, trans, zeroBalanceGuideText])

  const submitHandler = useCallback(async () => {
    if (hasZeroBalance && isRepaying) {
      const link =
        network === BlockchainNetwork.RENEC
          ? trans.dashboard.modal.not_enough_balance_link
          : trans.dashboard.modal.not_enough_balance_solana_token_link(selectedReserve?.mintAddress)
      window.open(link, "_blank")
      return
    }

    if (!selectedReserve) return

    setLoading(true)
    setHelperText({ content: "", color: "" })
    setNftErrorText("")

    const submitAmountNum = amount ?? 0
    const symbol = selectedReserve?.symbol ?? ""

    // When we repay max, we need to add a small amount to avoid we don't repay everything.
    const errorAdjustedAmount = submitAmountNum * (1 + REPAY_MAX_LIMIT_ERROR_PERCENTAGE)
    const shouldApplyErrorLimit =
      submitAmountNum === repayMax.toNumber() && selectedReserveBalance && errorAdjustedAmount < selectedReserveBalance
    const amountWithError = shouldApplyErrorLimit ? errorAdjustedAmount : submitAmountNum
    const roundedAmount = new BigNumber(amountWithError)
      .shiftedBy(selectedReserve?.decimals ?? DEFAULT_DECIMALS)
      .toFixed(0)

    const referrer = !isStringEmpty(referral.referrer) ? new PublicKey(referral.referrer) : undefined
    const nftVoucherAddress = isBorrowing
      ? selectedNFT?.borrowVoucher?.mintAddress
        ? new PublicKey(selectedNFT.borrowVoucher.mintAddress)
        : undefined
      : selectedNFT?.repayVoucher?.mintAddress
        ? new PublicKey(selectedNFT.repayVoucher.mintAddress)
        : undefined

    const voucherType = isBorrowing ? NFTVoucherType.BORROW : NFTVoucherType.REPAY
    const nftInterestAmount =
      refNFTInterestAmount.current[voucherType] < 0 ? 0 : refNFTInterestAmount.current[voucherType]
    const roundedNftInterestAmount = new BigNumber(nftInterestAmount)
      .shiftedBy(selectedReserve?.decimals ?? DEFAULT_DECIMALS)
      .toFixed(0)

    const result = await actionHandler({
      network,
      action: popupState.type === BORROW ? ActionEnum.BORROW : ActionEnum.REPAY,
      amount: roundedAmount,
      symbol,
      mintAddress: selectedReserve?.mintAddress,
      supplyToEarnApys,
      supplyToEarnRewards,
      referralAccount: referrer,
      nftVoucherAddress,
      nftInterestAmount: roundedNftInterestAmount,
    })

    // When we repay with NFT voucher, we need to add the interest amount to the amount to display
    const submitAmountToDisplay =
      selectedNFT && selectedNFT.repayVoucher
        ? new BigNumber(amountWithError ?? 0).plus(refNFTInterestAmount.current[NFTVoucherType.REPAY]).toNumber()
        : amountWithError

    await handleTransactionResult({
      result,
      popupState,
      amount: submitAmountToDisplay,
      selectedReserve,
      walletAddress: address,
      obligationAddress: selectedObligation?.address,
      actionMax: repayMax,
      buttonError,
      setTransactionDetail,
      setHelperText,
    })

    disableModal()
    setLoading(false)

    if (popupState.type === BORROW && result.isSucceeded) {
      const dataTracking = JSON.parse(trackingUTM)
      dataTracking.isMainnet = IS_MAINNET
      dataTracking.reserveSymbol = selectedReserve.symbol
      dataTracking.reserveAddress = selectedReserve.address
      dataTracking.walletAddress = address
      dataTracking.volume = roundedAmount
      dataTracking.market = network

      sendEventLog("user_borrow_success", dataTracking)
    }
  }, [
    hasZeroBalance,
    isRepaying,
    selectedReserve,
    amount,
    repayMax,
    selectedReserveBalance,
    referral.referrer,
    isBorrowing,
    selectedNFT,
    actionHandler,
    network,
    trans,
    popupState,
    supplyToEarnApys,
    supplyToEarnRewards,
    disableModal,
    handleTransactionResult,
    address,
    selectedObligation?.address,
    buttonError,
    trackingUTM,
  ])

  const isSubmitButtonDisabled = useMemo(
    () => loading || (Boolean(buttonError) && (!hasZeroBalance || !isRepaying)),
    [loading, buttonError, hasZeroBalance, isRepaying],
  )

  const lendingCollectionKeys = ConstantsHelper.getNFTCollectionKeys(network)
  const isShowNFTVoucherInput = useMemo(() => lendingCollectionKeys.length > 0, [lendingCollectionKeys.length])

  const titleDialog = useMemo(() => {
    if (!transactionDetail.isActive) return

    return transactionDetail.isError ? trans.dashboard.modal.oops : trans.dashboard.modal.congrats
  }, [trans, transactionDetail])

  if (!selectedReserve || !selectedPool) {
    return <></>
  }

  return (
    <RelendModal
      title={titleDialog}
      isSmallPopup={transactionDetail.isActive}
      hideButtonClose={transactionDetail.isActive}
      open={popupState.isOpen}
      handleClose={handleCloseModal}
      data-testid='modal-borrow-repay'
      useDrawerOnMobile
    >
      {transactionDetail.isActive && (
        <ModalTxResult transactionDetail={transactionDetail} handleCloseModal={handleCloseModal} />
      )}
      {!transactionDetail.isActive && (
        <Box>
          <Tabs
            value={tab}
            onChange={handleChangeTab}
            TabIndicatorProps={{ sx: { display: "none" } }}
            aria-label='modal-tabs-borrow-repay'
            variant='fullWidth'
            className='bg-white border border-solid border-secondary rounded-lg p-[2px]'
          >
            <Tab
              classes={{ selected: "bg-secondary !text-primary font-semibold rounded-lg" }}
              className='flex-1 py-2 text-sm sm:text-lg font-medium text-neutral-500'
              value={BORROW}
              label={trans.dashboard.modal.borrow}
              data-testid='modal-supply-tab'
              disabled={loading}
            />
            <Tab
              classes={{ selected: "bg-secondary !text-primary font-semibold rounded-lg" }}
              className='flex-1 py-2 text-sm sm:text-lg font-medium text-neutral-500'
              value={REPAY}
              label={trans.dashboard.modal.repay}
              data-testid='modal-withdraw-tab'
              disabled={loading}
            />
          </Tabs>

          {(!selectedObligation?.totalSupplyValue || selectedObligation?.totalSupplyValue.isLessThanOrEqualTo(0)) && (
            <EmptyRelendModalBody />
          )}

          {selectedObligation?.totalSupplyValue.isGreaterThan(0) && (
            <Box className='mt-4 sm:mt-6'>
              <ModalInput
                value={amount}
                setValue={setAmount}
                onValueChange={handleAmountChange}
                selectItems={SELECT_ITEMS}
                selectedReserve={selectedReserve}
                selectedValue={selectedReserve?.address}
                handleSelectChange={(value) => {
                  setSelectedReserve(value)
                  handleFetchReserveBalance()
                  setSelectedNFT({
                    [NFTVoucherType.BORROW]: undefined,
                    [NFTVoucherType.REPAY]: undefined,
                  })
                  refreshNFTCheckbox((prev) => !prev)
                }}
                maxValue={tab === BORROW ? borrowMax : repayMax}
                disabled={loading}
              />
              {isShowNFTVoucherInput && (
                <ModalNFT
                  selectedReserve={selectedReserve}
                  selectedNFT={selectedNFT}
                  setSelectedNFT={setSelectedNFT}
                  shouldRefreshCheckbox={shouldRefreshNftCheckbox}
                  refreshNFTCheckbox={refreshNFTCheckbox}
                  error={nftErrorText}
                  currentTab={tab}
                />
              )}
              <BorrowRepayModelStat
                selectedReserve={selectedReserve}
                value={amount}
                type={popupState.type}
                selectedNFT={selectedNFT}
                handleUpdateNFTInterestAmount={handleUpdateNFTInterestAmount}
              />
              <Box mb={3}>
                <LiquidationProgress
                  currentProgress={weightedBorrowOverSupply.toNumber()}
                  progressWidth={progressWidth}
                  borrowLimit={borrowLimitOverSupply.toNumber()}
                  liquidationThreshold={liquidationThresholdOverSupply.toNumber()}
                  setProgressWidth={setProgressWidth}
                />
              </Box>
              {helperText.content && (
                <Typography sx={{ ...styles.helperText, color: helperText.color }}>{helperText.content}</Typography>
              )}
              <Button
                sx={styles.buttonSubmit}
                variant='primary'
                fullWidth
                onClick={submitHandler}
                disabled={isSubmitButtonDisabled}
                loading={loading}
                data-testid='button-submit-borrow-repay-modal'
              >
                {submitButtonContent}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </RelendModal>
  )
}

export default View
